/**
 * usePlatformProfile — shared data hook for PlatformProfileLayout and all tabs.
 *
 * React Query dedupes requests by key, so every tab that calls this hook with
 * the same (id, region) gets the same cached data with no extra fetches.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiUrl } from '@/lib/api';
import { prettyQuarter } from '@/lib/format';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PlatformSummary {
  platform: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
  net_additions: number;
  churns: number;
  churn_rate: number;
  subscriber_change: number;
  arpu_change: number;
  revenue_change: number;
  region_count: number;
  latest_quarter: string;
}

export interface HistoryEntry {
  quarter: string;
  range_end_date: string;
  region: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
  /** null on the first quarter of the history: derived by LAG, no prior quarter (spec §2). */
  churns: number | null;
  net_additions: number | null;
  churn_rate: number | null;
}

/** Regions the streaming metrics are reported in. */
export const PROFILE_REGIONS = ['UCAN', 'EMEA', 'APAC', 'LATAM'] as const;

export interface RegionEntry {
  region: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
}

export interface PlatformLeaderboardRow {
  platform: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
  net_additions: number;
  churn_rate: number;
  retained_subs?: number;
  latest_quarter: string;
}

export interface PlatformTitleCount {
  availability_platform: string;
  title_count: number;
  total_revenue: number;
}

// ─── Fetchers ────────────────────────────────────────────────────────────────

async function fetchSummary(id: string): Promise<PlatformSummary> {
  const res = await fetch(apiUrl(`/platforms/${encodeURIComponent(id)}`));
  if (!res.ok) throw Object.assign(new Error('Platform not found'), { status: res.status });
  return res.json();
}

async function fetchHistory(id: string, region?: string): Promise<HistoryEntry[]> {
  const params = new URLSearchParams({ region: region ?? '' });
  const res = await fetch(apiUrl(`/platforms/${encodeURIComponent(id)}/history?${params}`));
  if (!res.ok) throw new Error('Failed to fetch platform history');
  const json = await res.json();
  // endpoint returns { data: [...] } envelope
  const rows = [...((json.data ?? json) as HistoryEntry[])].sort((a, b) => a.quarter.localeCompare(b.quarter));
  // The oldest quarter's churns / net adds / churn rate come from LAG with no
  // prior row, so they read 0 and gross adds. Blank them rather than show them.
  return rows.map((r, i) => (i === 0 ? { ...r, churns: null, net_additions: null, churn_rate: null } : r));
}

/** History for every region in parallel — same keys as the region-scoped query, so cached once. */
export function useRegionHistories(id: string) {
  const results = useQueries({
    queries: PROFILE_REGIONS.map((r) => ({
      queryKey: ['platform-history', id, r],
      queryFn: () => fetchHistory(id, r),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const byRegion: Partial<Record<string, HistoryEntry[]>> = {};
  results.forEach((q, i) => {
    if (q.data) byRegion[PROFILE_REGIONS[i]] = q.data;
  });
  return { byRegion, isLoading: results.some((q) => q.isLoading) };
}

async function fetchRegions(id: string): Promise<RegionEntry[]> {
  const res = await fetch(apiUrl(`/platforms/${encodeURIComponent(id)}/regions`));
  if (!res.ok) throw new Error('Failed to fetch platform regions');
  const json = await res.json();
  return (json.data ?? json) as RegionEntry[];
}

async function fetchLeaderboard(region?: string, endDate?: string): Promise<PlatformLeaderboardRow[]> {
  const params = new URLSearchParams();
  if (region) params.set('region', region);
  if (endDate) params.set('endDate', endDate);
  const res = await fetch(apiUrl(`/platforms/leaderboard?${params}`));
  if (!res.ok) throw new Error('Failed to fetch platform leaderboard');
  const json = await res.json();
  return (json.data ?? json) as PlatformLeaderboardRow[];
}

async function fetchTitleCounts(): Promise<PlatformTitleCount[]> {
  const res = await fetch(apiUrl('/platforms'));
  if (!res.ok) throw new Error('Failed to fetch platform list');
  const json = await res.json();
  return (json.data ?? json) as PlatformTitleCount[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePlatformProfile(id: string, region?: string) {
  const STALE = 5 * 60 * 1000;

  const summary = useQuery({
    queryKey: ['platform-summary', id],
    queryFn: () => fetchSummary(id),
    enabled: !!id,
    staleTime: STALE,
    retry: (count, err) => {
      // Don't retry 404s — platform has no streaming metrics.
      if ((err as { status?: number }).status === 404) return false;
      return count < 2;
    },
  });

  // Full history (all regions) — FE slices client-side (see spec §2).
  const historyAll = useQuery({
    queryKey: ['platform-history', id, ''],
    queryFn: () => fetchHistory(id, ''),
    enabled: !!id,
    staleTime: STALE,
  });

  // Region-scoped history — only when a region filter is active.
  const historyRegion = useQuery({
    queryKey: ['platform-history', id, region],
    queryFn: () => fetchHistory(id, region),
    enabled: !!id && !!region,
    staleTime: STALE,
  });

  const regions = useQuery({
    queryKey: ['platform-regions', id],
    queryFn: () => fetchRegions(id),
    enabled: !!id,
    staleTime: STALE,
  });

  // Latest-quarter leaderboard — for ranks.
  const leaderboard = useQuery({
    queryKey: ['platform-leaderboard', region ?? ''],
    queryFn: () => fetchLeaderboard(region),
    enabled: !!id,
    staleTime: STALE,
  });

  // Prior-quarter leaderboard — for rank movement. The quarter before the
  // latest comes from the all-regions history (every region shares quarters).
  const allSorted = [...(historyAll.data ?? [])].sort((a, b) => a.quarter.localeCompare(b.quarter));
  const priorEndDate = allSorted[allSorted.length - 2]?.range_end_date;
  const leaderboardPrior = useQuery({
    queryKey: ['platform-leaderboard', region ?? '', priorEndDate],
    queryFn: () => fetchLeaderboard(region, priorEndDate),
    enabled: !!id && !!priorEndDate,
    staleTime: STALE,
  });

  const titleCounts = useQuery({
    queryKey: ['platform-title-counts'],
    queryFn: fetchTitleCounts,
    enabled: !!id,
    staleTime: STALE,
  });

  // ── Derived: history sliced by range (client-side) ──────────────────────
  // Use region-scoped history when region is set; fall back to all-regions.
  // No fallback to all-regions while a region loads — that would show numbers
  // under the wrong scope label. Widgets read '—' for that moment instead.
  const rawHistory = (region ? historyRegion.data : historyAll.data) ?? [];

  // Sort oldest→newest; spec §2: first row's derived fields (churn, net adds) are
  // wrong (LAG caveat) — we expose them but callers must suppress the first row's
  // derived columns.
  const history = [...rawHistory].sort((a, b) => a.quarter.localeCompare(b.quarter));

  // ── Derived: rank among platforms (latest + prior quarter) ─────────────
  // Churn ranks ascending — lower churn is the better rank.
  type RankKey = 'subscribers' | 'revenue' | 'arpu' | 'churn_rate';
  const rankIn = (rows: PlatformLeaderboardRow[] | undefined, key: RankKey) => {
    if (!rows?.length) return undefined;
    const dir = key === 'churn_rate' ? 1 : -1;
    const idx = [...rows].sort((a, b) => dir * (a[key] - b[key])).findIndex((r) => r.platform === id);
    return idx >= 0 ? idx + 1 : undefined;
  };
  const board = leaderboard.data;
  const prior = leaderboardPrior.data;
  const ranks = {
    subscribers: rankIn(board, 'subscribers'),
    revenue: rankIn(board, 'revenue'),
    arpu: rankIn(board, 'arpu'),
    churn: rankIn(board, 'churn_rate'),
    total: board?.length ?? 0,
  };

  // Places climbed since the prior quarter (positive = better), d360's rule.
  const movement = (key: RankKey, now: number | undefined) => {
    const before = rankIn(prior, key);
    return now !== undefined && before !== undefined ? before - now : undefined;
  };
  const rankMovement = {
    subscribers: movement('subscribers', ranks.subscribers),
    revenue: movement('revenue', ranks.revenue),
    arpu: movement('arpu', ranks.arpu),
    churn: movement('churn_rate', ranks.churn),
  };

  // ── Derived: latest-quarter figures in the current region scope ─────────
  // From history so a region filter applies (the summary endpoint has none).
  const latest = history[history.length - 1];
  const previous = history[history.length - 2];
  const pctChange = (now?: number, before?: number) =>
    now !== undefined && before ? ((now - before) / before) * 100 : undefined;
  const current = latest && {
    subscribers: latest.subscribers,
    revenue: latest.revenue,
    arpu: latest.arpu,
    churnRate: latest.churn_rate,
  };
  const changes = {
    subscribers: pctChange(latest?.subscribers, previous?.subscribers),
    revenue: pctChange(latest?.revenue, previous?.revenue),
    arpu: pctChange(latest?.arpu, previous?.arpu),
    // Churn moves in percentage points; the first row's churn is a LAG artefact.
    churnPts:
      latest?.churn_rate != null && previous?.churn_rate != null ? latest.churn_rate - previous.churn_rate : undefined,
  };

  // ── Derived: data span ───────────────────────────────────────────────────
  const firstQuarter = history[0]?.quarter;
  const latestQuarter = history[history.length - 1]?.quarter ?? summary.data?.latest_quarter;
  const dataSpan =
    firstQuarter && latestQuarter
      ? `${prettyQuarter(firstQuarter)} – ${prettyQuarter(latestQuarter)}`
      : '';

  // ── Derived: title count ─────────────────────────────────────────────────
  const titleCountRow = titleCounts.data?.find((r) => r.availability_platform === id);
  const titleCount = titleCountRow?.title_count;

  return {
    // Raw query states
    summary,
    historyAll,
    historyRegion,
    regions,
    leaderboard,
    leaderboardPrior,
    titleCounts,
    // Unwrapped data (convenience)
    summaryData: summary.data,
    // Derived
    history,      // sorted, region-aware
    ranks,
    rankMovement,
    current,
    changes,
    dataSpan,
    latestQuarter,
    titleCount,
    // Loading / error helpers
    isLoading: summary.isLoading || historyAll.isLoading,
    isError: summary.isError,
    notFound: summary.error && (summary.error as { status?: number }).status === 404,
  };
}
